import type {
    DescriptionKeyword,
    DescriptionKeywordCategoriesMap,
    DescriptionKeywordKeyNames,
    KeywordToken,
} from "@reva/shared/description-keywords";

export type DescriptionKeywordTooltipKind = "poi" | "noise" | "emission" | "none";

// type_en with old and corrected spelling side by side, AVM LIVE lags behind STAGE (#19887).
export const DESCRIPTION_KEYWORD_POI_TYPES: Record<string, string[]> = {
    oepnv: ["Railway station", "Bus stop", "Mainline station", "Subway station", "Tram stop", "Light rail station"],
    idv: [
        "Federal highway junction",
        "Motorway link",
        "Motorway link (single-digit)",
        "Motorway link (two-digit)",
        "Motorway link (three-digit)",
    ],
    geschaefte: ["Bank", "Bakery/Butchery", "Mall", "Shop", "Post Office", "Supermarket"],
    einrichtungen: [
        "Pharmacie",
        "Pharmacy",
        "Doctor",
        "Hospital",
        "Kindergarten",
        "Primary school",
        "Secondary school",
        "Mixed school",
        "University",
    ],
    naherholung: [
        "Park",
        "Forest",
        "Fließende Gewässer",
        "Stehende Gewässer",
        "Flowing water",
        "Standing water",
        "Playground",
        "Sport",
    ],
};

export function tooltipKindForKeyword(keyName: string | undefined): DescriptionKeywordTooltipKind {
    if (keyName == null) return "none";
    if (keyName in DESCRIPTION_KEYWORD_POI_TYPES) return "poi";
    if (keyName === "laerm") return "noise";
    if (keyName === "emission") return "emission";
    return "none";
}

export function isValidKeyword(
    text: string | null | undefined,
    keyword: DescriptionKeyword | null | undefined,
    descriptionKeywordCategories: DescriptionKeywordCategoriesMap,
    descriptionKeywordKeyNames: DescriptionKeywordKeyNames,
): boolean {
    if (!text || !keyword || keyword.offset == null || keyword.category_number == null || keyword.length == null)
        return false;
    if (keyword.offset < 0 || keyword.length <= 0 || keyword.offset + keyword.length > text.length) return false;
    return (
        tooltipKindForKeyword(descriptionKeywordKeyNames[keyword.key]) !== "none" ||
        (descriptionKeywordCategories[keyword.key]?.length ?? 0) > 1
    );
}

const areOverlapping = (keyword1: {offset: number; length: number}, keyword2: {offset: number}) =>
    keyword1.offset + keyword1.length > keyword2.offset;

/** Gültige Keywords, sortiert nach Textposition und ohne Überlappungen. */
export function removeInvalidKeywords(
    text: string | null | undefined,
    keywords: DescriptionKeyword[] | null | undefined,
    descriptionKeywordCategories: DescriptionKeywordCategoriesMap,
    descriptionKeywordKeyNames: DescriptionKeywordKeyNames,
) {
    if (!text || !keywords) return [];
    const sorted = keywords
        .filter((keyword) => isValidKeyword(text, keyword, descriptionKeywordCategories, descriptionKeywordKeyNames))
        .map((keyword) => ({
            ...keyword,
            offset: keyword.offset as number,
            length: keyword.length as number,
            category_number: keyword.category_number as number,
        }))
        .sort((a, b) => a.offset - b.offset);
    return sorted.filter((keyword, i) => i === 0 || !areOverlapping(sorted[i - 1], keyword));
}

/** Ersetzt jedes gültige Keyword im Text durch ein `[key:category_number]`-Token; `null`, wenn nichts zu markieren ist. */
export function tokenizeText(
    text: string | null | undefined,
    keywords: DescriptionKeyword[] | null | undefined,
    descriptionKeywordCategories: DescriptionKeywordCategoriesMap,
    descriptionKeywordKeyNames: DescriptionKeywordKeyNames,
): string | null {
    if (!text) return null;
    const validKeywords = removeInvalidKeywords(
        text,
        keywords,
        descriptionKeywordCategories,
        descriptionKeywordKeyNames,
    );
    if (validKeywords.length === 0) return null;

    let templatedText = "";
    let cursor = 0;
    for (const keyword of validKeywords) {
        templatedText += `${text.slice(cursor, keyword.offset)}[${keyword.key}:${keyword.category_number}]`;
        cursor = keyword.offset + keyword.length;
    }
    return templatedText + text.slice(cursor);
}

export function replaceKeywordToken(templatedText: string, token: KeywordToken, newCategoryNumber: number): string {
    return (
        templatedText.slice(0, token.matchStart) +
        `[${token.key}:${newCategoryNumber}]` +
        templatedText.slice(token.matchEnd)
    );
}

export interface InvalidKeywordMarker {
    matchStart: number;
    matchEnd: number;
}

/** Erste Klammer in `text` ohne Gegenpart (z.B. weil im Freitext versehentlich eine einzelne `[` oder `]`
 * gelöscht wurde) — `null`, wenn Anzahl `[` und `]` übereinstimmen und alle paarig sind. */
function findUnmatchedBracketIndex(text: string): number | null {
    const openIndexes: number[] = [];
    for (let i = 0; i < text.length; i++) {
        if (text[i] === "[") openIndexes.push(i);
        else if (text[i] === "]") {
            if (openIndexes.length === 0) return i;
            openIndexes.pop();
        }
    }
    return openIndexes[0] ?? null;
}

// Zum Eingrenzen der fehlerhaften Stelle für die Fehlermeldung: bis zur nächsten Whitespace-Grenze
// ausdehnen (nicht z.B. bis zum nächsten Nicht-Ziffern-Zeichen — der Fließtext um den kaputten Marker
// herum kann selbst Ziffern enthalten, die nicht zum Marker gehören).
function expandMarkerFragment(text: string, index: number): InvalidKeywordMarker {
    let matchStart = index;
    let matchEnd = index + 1;
    while (matchStart > 0 && !/\s/.test(text[matchStart - 1])) matchStart--;
    while (matchEnd < text.length && !/\s/.test(text[matchEnd])) matchEnd++;
    return {matchStart, matchEnd};
}

// Bewusst laxer als KEYWORD_TOKEN_PATTERN: findet auch inhaltlich kaputte Marker (z.B. fehlender
// Doppelpunkt nach versehentlichem Löschen im Freitext), nicht nur wohlgeformte Tokens. Wird erst
// angewendet, nachdem feststeht, dass alle Klammern paarig sind (sonst siehe findUnmatchedBracketIndex).
const KEYWORD_MARKER_ATTEMPT_PATTERN = /\[[^[\]]*]?/g;
const KEYWORD_TOKEN_STRICT_PATTERN = /^\[(\d+):(\d+)]$/;

/** Erster ungültiger Marker in `text`: entweder eine Klammer ohne Gegenpart (Anzahl `[` und `]` stimmt
 * nicht überein), ein Marker, der nicht dem `[key:category_number]`-Format entspricht, oder einer mit
 * category_number, die sich nicht auf eine bekannte Kategorie seines Keys abbilden lässt — `null`, wenn
 * alle Marker gültig sind. */
export function findInvalidKeywordToken(
    text: string,
    descriptionKeywordCategories: DescriptionKeywordCategoriesMap,
): InvalidKeywordMarker | null {
    const unmatchedBracketIndex = findUnmatchedBracketIndex(text);
    if (unmatchedBracketIndex != null) return expandMarkerFragment(text, unmatchedBracketIndex);

    for (const attempt of text.matchAll(KEYWORD_MARKER_ATTEMPT_PATTERN)) {
        const matchStart = attempt.index;
        const matchEnd = matchStart + attempt[0].length;
        const strictMatch = attempt[0].match(KEYWORD_TOKEN_STRICT_PATTERN);
        if (!strictMatch) return {matchStart, matchEnd};
        const category = (descriptionKeywordCategories[Number(strictMatch[1])] ?? []).find(
            (c) => c.categoryNumber === Number(strictMatch[2]),
        );
        if (!category) return {matchStart, matchEnd};
    }
    return null;
}

export interface TextRange {
    start: number;
    end: number;
}

interface Word extends TextRange {
    text: string;
}

// Whitespace bleibt als eigenes "Wort" erhalten, damit ein [key:category_number]-Marker (enthält
// selbst keinen Whitespace) beim Diffen als atomare Einheit behandelt wird statt zerlegt zu werden.
function splitWords(text: string): Word[] {
    return [...text.matchAll(/\S+|\s+/g)].map((match) => ({
        text: match[0],
        start: match.index,
        end: match.index + match[0].length,
    }));
}

/** LCS-Länge von `a[i:]`/`b[j:]` für jedes (i, j), rückwärts befüllt. */
function lcsLengths(a: Word[], b: Word[]): number[][] {
    const lengths = Array.from({length: a.length + 1}, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            lengths[i][j] =
                a[i].text === b[j].text ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
        }
    }
    return lengths;
}

/**
 * Wort-Diff (LCS) zwischen `oldText` und `newText`: die Bereiche in `newText`, die gegenüber
 * `oldText` neu sind oder geändert wurden -- als sortierte, nicht überlappende Ranges, benachbarte
 * Änderungen zusammengefasst. Reine Löschungen (ein Wort nur in `oldText`) liefern keine Range,
 * nur was tatsächlich noch in `newText` steht wird hervorgehoben. Für die Diff-Hervorhebung in
 * DescriptionRichTextField -- eigene schlanke Umsetzung statt einer Diff-Library, weil hier
 * ausschließlich "welche Wörter sind neu" gebraucht wird.
 */
export function addedRanges(oldText: string, newText: string): TextRange[] {
    const oldWords = splitWords(oldText);
    const newWords = splitWords(newText);
    const lengths = lcsLengths(oldWords, newWords);

    const ranges: TextRange[] = [];
    const addWord = (word: Word) => {
        const last = ranges[ranges.length - 1];
        if (last && last.end === word.start) last.end = word.end;
        else ranges.push({start: word.start, end: word.end});
    };

    // Standard-LCS-Backtracking von vorne: bei Gleichheit ist das Wort in der LCS (kein Diff);
    // sonst dem größeren LCS-Rest folgen -- oldWords[i] wurde gelöscht (überspringen) oder
    // newWords[j] ist neu/geändert (addWord).
    let i = 0;
    let j = 0;
    while (i < oldWords.length && j < newWords.length) {
        if (oldWords[i].text === newWords[j].text) {
            i++;
            j++;
        } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
            i++;
        } else {
            addWord(newWords[j]);
            j++;
        }
    }
    while (j < newWords.length) addWord(newWords[j++]); // Rest von newText ohne Gegenstück in oldText
    return ranges;
}
